import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import {
  ArticleCategoryDTO,
  MultiProductNodeDto,
  MultiProductNodeDtoRequestedTypeEnum,
  OrganizationDto,
  OtherArticleNamesDto,
  ProductSearchResultDto
} from '@scip/api';
import { ProductControllerService } from '@scip/api';
import { SelectItem } from 'primeng/api';

import { translationKeys } from '../../../../core';
import { BackendErrorService, EnumsService, NotificationService, ProfileService } from '../../../../services';
import { TableColumnConfiguration, TableConfiguration } from '@avano/avano-components/avano-table';
import { typeField } from '@avano/avano-components/utils';

@Component({
  selector: 'scip-edit-product',
  templateUrl: './edit-product-dialog.component.html',
  styleUrls: ['./edit-product-dialog.component.scss']
})
export class EditProductDialogComponent implements OnInit {
  @Input() products: ProductSearchResultDto[];

  @Output() readonly closeEvent: EventEmitter<boolean> = new EventEmitter<boolean>();

  articleCategories: ArticleCategoryDTO[] = [];
  columns: TableColumnConfiguration[];
  displayArticleCategorySelection = false;
  displayOtherNameSelection = false;
  inEUTypeSuggestions: SelectItem[] = [];
  organizationsSuggestions: OrganizationDto[] = [];
  otherArticleNames: OtherArticleNamesDto[] = [];
  provideSUI: boolean;
  //TODO: change to KeyValues type
  requestedTypes: { name: string; key: MultiProductNodeDtoRequestedTypeEnum }[] = [
    { name: translationKeys.dossierDto.requestedType.STANDARD, key: MultiProductNodeDtoRequestedTypeEnum.Standard },
    { name: translationKeys.dossierDto.requestedType.PARTS_ONLY, key: MultiProductNodeDtoRequestedTypeEnum.PartsOnly },
    { name: translationKeys.dossierDto.requestedType.REFERENCING, key: MultiProductNodeDtoRequestedTypeEnum.Referencing }
  ];
  safeUseInstructionsText: string;
  selectedEUType: SelectItem;
  selectedOrganization: OrganizationDto;
  selectedRequestedType: MultiProductNodeDtoRequestedTypeEnum;
  showDialog = true;
  translationKeys: typeof translationKeys = translationKeys;

  tableConfig: TableConfiguration = { tableSettings: { tableCollapsable: true } };
  columnKeys = {
    name: typeField<ProductSearchResultDto>('name'),
    number: typeField<ProductSearchResultDto>('number')
  };

  private allOrganizations: OrganizationDto[] = [];

  constructor(
    private enumService: EnumsService,
    private profileService: ProfileService,
    private productControllerService: ProductControllerService,
    private backendErrorService: BackendErrorService,
    private notificationService: NotificationService
  ) {}

  ngOnInit(): void {
    this.initializeOrganizationData();

    this.columns = [
      { header: translationKeys.editProductDialogComponent.html.productsTable.column.productName, field: this.columnKeys.name },
      { header: translationKeys.editProductDialogComponent.html.productsTable.column.productNumber, field: this.columnKeys.name }
    ];
  }

  closeArticleCategoryDialog(): void {
    this.displayArticleCategorySelection = false;
  }

  closeOtherNameDialog(): void {
    this.displayOtherNameSelection = false;
  }

  cancelDialog(): void {
    this.closeEvent.emit(false);
    this.showDialog = false;
  }

  filterInEUTypes(event: { originalEvent: Event; query: string }): void {
    this.inEUTypeSuggestions = this.enumService.filterInEUTypes(event);
  }

  filterOrganizations(event: { query: string }): void {
    this.organizationsSuggestions = this.allOrganizations.filter((singleOrg: OrganizationDto) => {
      return singleOrg.name.toLowerCase().startsWith(event.query.toLowerCase());
    });
    this.organizationsSuggestions = this.organizationsSuggestions.sort((a, b) => (a.name > b.name ? 1 : b.name > a.name ? -1 : 0));
  }

  openInformationArticleCategoryDialog(): void {
    this.displayArticleCategorySelection = true;
  }

  openOtherNameDialog(): void {
    this.displayOtherNameSelection = true;
  }

  saveArticleCategory(categories: ArticleCategoryDTO[]): void {
    this.articleCategories = [...categories];
    this.displayArticleCategorySelection = false;
  }

  saveOtherName(otherNames: OtherArticleNamesDto[]): void {
    this.otherArticleNames = [...otherNames];
    this.displayOtherNameSelection = false;
  }

  saveProductEdits(): void {
    let hasPayload = false; // Flag if there is a change to be saved

    const multipleProductDetails: MultiProductNodeDto = {};
    multipleProductDetails.identifers = this.products.map((singleProduct: ProductSearchResultDto) => singleProduct.identifier);
    multipleProductDetails.overwriteCategories = true; // TODO: Everytime true? Really? Discussion with backend needed
    multipleProductDetails.productDetails = {
      articleCategories: this.articleCategories,
      otherArticleNames: this.otherArticleNames
    };

    // TODO: Refactor after discussion with backend, if it was needed that even
    // for empty articleCategories/otherArticleNames an empty array is needed or not
    if (this.otherArticleNames.length >= 1 || this.articleCategories.length >= 1) {
      hasPayload = true;
    }
    if (this.provideSUI !== undefined) {
      multipleProductDetails.productDetails.provideSafeUseInstruction = this.provideSUI;
      hasPayload = true;
    }
    if (this.safeUseInstructionsText?.length) {
      multipleProductDetails.productDetails.customSUIText = this.safeUseInstructionsText;
      hasPayload = true;
    }
    if (this.selectedEUType) {
      multipleProductDetails.productDetails.productionInEU = this.selectedEUType.value;
      hasPayload = true;
    }
    if (this.selectedRequestedType) {
      multipleProductDetails.requestedType = this.selectedRequestedType;
      hasPayload = true;
    }
    if (this.selectedOrganization) {
      multipleProductDetails.organizationId = this.selectedOrganization.identifier;
      hasPayload = true;
    }

    if (hasPayload) {
      this.handleBackendCall(multipleProductDetails);
    } else {
      this.closeEvent.emit(false);
    }
  }

  private handleBackendCall(payload: MultiProductNodeDto): void {
    this.productControllerService.editProducts(payload).subscribe(
      () => {
        this.notificationService.showSuccess(translationKeys.editProductDialogComponent.html.msg.saveSuccess);
        this.closeEvent.emit(true);
      },
      (error) => {
        if (error.status === 423) {
          this.backendErrorService.handleError(error, translationKeys.editProductDialogComponent.html.msg.productInProgressError);
        } else {
          this.backendErrorService.handleError(error, translationKeys.editProductDialogComponent.html.msg.productsUpdateError);
        }
      }
    );
  }

  private initializeOrganizationData(): void {
    this.profileService.getAllOrganizations(true, true, true).subscribe((allOrgs) => {
      this.allOrganizations = allOrgs;
    });
  }
}
